import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, Chip, Divider, Paper, Stack, Typography } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import type {
  FieldMapping,
  MappingResponse,
  TransformationStep,
} from '../../services/api';
import type { PipelineSourceField, StepConfig } from './types';
import { useVisualPipeline } from './useVisualPipeline';
import { quoteIdentifier, unquoteIdentifier } from './expressionSerializer';
import SourceFieldSelector from './SourceFieldSelector';
import TransformationPipeline from './TransformationPipeline';
import AddStepMenu from './AddStepMenu';
import LivePreviewStrip from './LivePreviewStrip';
import AdvancedExpressionEditor from './AdvancedExpressionEditor';

interface VisualMappingBuilderProps {
  mapping: FieldMapping;
  activeStep?: TransformationStep;
  mappingResponse?: MappingResponse;
  /** Called whenever the expression changes */
  onExpressionChange: (expression: string) => void;
}

/**
 * Extract available source fields from the mapping response's source schema snapshot.
 * Falls back to the current mapping's source field.
 */
const extractSourceFields = (
  mapping: FieldMapping,
  mappingResponse?: MappingResponse
): PipelineSourceField[] => {
  const fields: PipelineSourceField[] = [];
  const seen = new Set<string>();

  // 1. Add the mapping's own source field first
  if (mapping.source_field) {
    const id = `sf-${mapping.source_file || 'source'}-${mapping.source_field}`;
    fields.push({
      id,
      sourceFile: mapping.source_file || 'source',
      sourceField: mapping.source_field,
      qualifiedName: `${mapping.source_file || 'source'}.${mapping.source_field}`,
    });
    seen.add(mapping.source_field.toLowerCase());
  }

  // 2. Try to get all fields from the same source table via schema snapshot
  const metadata = mappingResponse?.metadata;
  const snapshot = metadata?.source_schema_set_snapshot;
  if (snapshot?.tables && mapping.source_file) {
    const sourceTable = mapping.source_file;
    // Try exact match, then lowercase match
    const tableEntry =
      snapshot.tables[sourceTable] ??
      Object.entries(snapshot.tables).find(
        ([key]: [string, any]) => key.toLowerCase() === sourceTable.toLowerCase()
      )?.[1];

    if (tableEntry && Array.isArray((tableEntry as any).columns)) {
      for (const col of (tableEntry as any).columns) {
        const colName = typeof col === 'string' ? col : col?.name;
        if (!colName || seen.has(colName.toLowerCase())) continue;
        seen.add(colName.toLowerCase());
        fields.push({
          id: `sf-${sourceTable}-${colName}`,
          sourceFile: sourceTable,
          sourceField: colName,
          qualifiedName: `${sourceTable}.${colName}`,
        });
      }
    }
  }

  // 2b. Fallback: check source_schema_signature (sources array format)
  const schemaSig = metadata?.source_schema_signature;
  if (schemaSig && typeof schemaSig === 'object' && mapping.source_file) {
    const sourceTable = mapping.source_file;
    // New format: { hash, sources: [{ source, columns: [{ name, data_type }] }] }
    const sources = schemaSig.sources ?? schemaSig;
    if (Array.isArray(sources)) {
      const matchedSource = sources.find(
        (s: any) =>
          (s.source || s.name)?.toLowerCase() === sourceTable.toLowerCase()
      );
      if (matchedSource?.columns && Array.isArray(matchedSource.columns)) {
        for (const col of matchedSource.columns) {
          const colName = col.name || col.column_name;
          if (!colName || seen.has(colName.toLowerCase())) continue;
          seen.add(colName.toLowerCase());
          fields.push({
            id: `sf-sig-${sourceTable}-${colName}`,
            sourceFile: sourceTable,
            sourceField: colName,
            qualifiedName: `${sourceTable}.${colName}`,
          });
        }
      }
    } else {
      // Legacy format: { tableName: [col1, col2, ...] }
      const rawSigFields =
        schemaSig[sourceTable] ??
        Object.entries(schemaSig).find(
          ([key]) => key.toLowerCase() === sourceTable.toLowerCase()
        )?.[1];
      const sigFields: string[] = Array.isArray(rawSigFields) ? rawSigFields as string[] : [];
      if (Array.isArray(sigFields)) {
        for (const colName of sigFields) {
          if (!colName || seen.has(colName.toLowerCase())) continue;
          seen.add(colName.toLowerCase());
          fields.push({
            id: `sf-sig-${sourceTable}-${colName}`,
            sourceFile: sourceTable,
            sourceField: colName,
            qualifiedName: `${sourceTable}.${colName}`,
          });
        }
      }
    }
  }

  // 2c. Scan other mappings with the same source_file for additional fields
  if (mappingResponse?.mappings && mapping.source_file) {
    const sourceTable = mapping.source_file;
    for (const m of mappingResponse.mappings) {
      if (
        m.source_file?.toLowerCase() === sourceTable.toLowerCase() &&
        m.source_field &&
        !seen.has(m.source_field.toLowerCase())
      ) {
        seen.add(m.source_field.toLowerCase());
        fields.push({
          id: `sf-peer-${sourceTable}-${m.source_field}`,
          sourceFile: sourceTable,
          sourceField: m.source_field,
          qualifiedName: `${sourceTable}.${m.source_field}`,
        });
      }
    }
  }

  // 3. Also add fields referenced in transformation step inputs
  if (mapping.transformation_steps) {
    for (const step of mapping.transformation_steps) {
      for (const input of step.inputs ?? []) {
        const fieldName = input.includes('.') ? input.split('.').pop()! : input;
        if (!fieldName || seen.has(fieldName.toLowerCase())) continue;
        seen.add(fieldName.toLowerCase());
        fields.push({
          id: `sf-step-${fieldName}`,
          sourceFile: mapping.source_file || 'source',
          sourceField: fieldName,
          qualifiedName: input,
        });
      }
    }
  }

  // 4. Extract identifiers from transformation expression (covers CONCAT etc. where step inputs are incomplete)
  if (mapping.transformation) {
    const exprStr = mapping.transformation;
    // Find unquoted identifiers that look like column references (skip SQL keywords and function names)
    const reserved = new Set(['CONCAT', 'CASE', 'WHEN', 'THEN', 'ELSE', 'END', 'CAST', 'AS',
      'COALESCE', 'UPPER', 'LOWER', 'TRIM', 'REPLACE', 'SPLIT', 'NULL', 'TRUE', 'FALSE',
      'IF', 'AND', 'OR', 'NOT', 'IS', 'IN', 'LIKE', 'BETWEEN', 'NVL', 'ROUND', 'ABS',
      'LEFT', 'RIGHT', 'SUBSTRING', 'LENGTH', 'LPAD', 'RPAD', 'TO_DATE', 'TO_CHAR',
      'DATE_FORMAT', 'PARSE_DATE', 'TO_TIMESTAMP', 'LITERAL', 'IS_NULL', 'IS_NOT_NULL']);

    // Extract double-quoted identifiers first (field names with spaces/special chars)
    const quotedIdentMatches = exprStr.match(/"([^"]+)"/g) ?? [];
    for (const qm of quotedIdentMatches) {
      const ident = unquoteIdentifier(qm);
      if (!ident || seen.has(ident.toLowerCase())) continue;
      if (mapping.source_file) {
        seen.add(ident.toLowerCase());
        fields.push({
          id: `sf-expr-${ident}`,
          sourceFile: mapping.source_file,
          sourceField: ident,
          qualifiedName: `${mapping.source_file}.${ident}`,
        });
      }
    }

    // Strip quoted strings (both single and double), then find unquoted word-boundary identifiers
    const stripped = exprStr.replace(/'[^']*'/g, '').replace(/"[^"]*"/g, '');
    const identifiers = stripped.match(/\b([A-Za-z_]\w*)\b/g) ?? [];
    for (const ident of identifiers) {
      if (reserved.has(ident.toUpperCase()) || seen.has(ident.toLowerCase())) continue;
      if (mapping.source_file) {
        seen.add(ident.toLowerCase());
        fields.push({
          id: `sf-expr-${ident}`,
          sourceFile: mapping.source_file,
          sourceField: ident,
          qualifiedName: `${mapping.source_file}.${ident}`,
        });
      }
    }
  }

  return fields;
};

const VisualMappingBuilder: React.FC<VisualMappingBuilderProps> = ({
  mapping,
  activeStep,
  mappingResponse,
  onExpressionChange,
}) => {
  const availableSourceFields = useMemo(
    () => extractSourceFields(mapping, mappingResponse),
    [mapping, mappingResponse]
  );

  const initialExpression = mapping.transformation ?? '';

  const {
    pipeline,
    expression,
    addStep,
    removeStep,
    updateStepConfig,
    reorderSteps,
    setAdvancedMode,
    setExpressionDirectly,
    resetPipeline,
  } = useVisualPipeline(initialExpression, availableSourceFields);

  // Sync expression changes back to the parent
  useEffect(() => {
    if (expression !== initialExpression) {
      onExpressionChange(expression);
    }
  }, [expression, initialExpression, onExpressionChange]);

  const handleAdvancedExpressionChange = useCallback(
    (expr: string) => {
      setExpressionDirectly(expr);
      onExpressionChange(expr);
    },
    [setExpressionDirectly, onExpressionChange]
  );

  const handleSourceFieldSelect = useCallback(
    (field: PipelineSourceField) => {
      setSelectedSourceFieldId(field.id);
      // When a source field is selected, if there are no steps, just set the expression to the field name
      if (pipeline.steps.length === 0) {
        onExpressionChange(quoteIdentifier(field.sourceField));
      }
    },
    [pipeline.steps.length, onExpressionChange]
  );

  const handleResetToVisual = useCallback(() => {
    resetPipeline();
    onExpressionChange('');
  }, [resetPipeline, onExpressionChange]);

  const sampleData = mappingResponse?.sample_output;
  const primarySourceField = pipeline.sourceFields[0];

  const [selectedSourceFieldId, setSelectedSourceFieldId] = useState<string | undefined>(
    () => primarySourceField?.id ?? availableSourceFields[0]?.id,
  );

  useEffect(() => {
    setSelectedSourceFieldId(primarySourceField?.id ?? availableSourceFields[0]?.id);
  }, [mapping.mapping_id]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Stack spacing={1.5}>
      {/* Header: Source -> Target */}
      <Paper
        variant="outlined"
        sx={{
          px: 1.5,
          py: 1,
          bgcolor: 'grey.50',
          borderRadius: 1.5,
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
        >
          <Chip
            label={mapping.source_field || 'source'}
            size="small"
            sx={{
              bgcolor: '#10b98122',
              color: '#059669',
              fontFamily: 'monospace',
              fontSize: '0.75rem',
              fontWeight: 600,
              height: 26,
              borderRadius: 1,
              border: '1px solid #10b98155',
            }}
          />
          <ArrowForwardIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
          {pipeline.steps.length > 0 && (
            <>
              <Chip
                label={`${pipeline.steps.length} step${pipeline.steps.length > 1 ? 's' : ''}`}
                size="small"
                variant="outlined"
                color="primary"
                sx={{ height: 22, fontSize: '0.7rem' }}
              />
              <ArrowForwardIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
            </>
          )}
          <Chip
            label={mapping.target_field || 'target'}
            size="small"
            sx={{
              bgcolor: '#2563eb22',
              color: '#1d4ed8',
              fontFamily: 'monospace',
              fontSize: '0.75rem',
              fontWeight: 600,
              height: 26,
              borderRadius: 1,
              border: '1px solid #2563eb55',
            }}
          />
        </Stack>
      </Paper>

      {/* Advanced mode banner */}
      {pipeline.isAdvancedMode && (
        <Alert
          severity="info"
          sx={{ py: 0.25, '& .MuiAlert-message': { fontSize: '0.78rem' } }}
          action={
            <Button
              size="small"
              startIcon={<RestartAltIcon />}
              onClick={handleResetToVisual}
              sx={{ textTransform: 'none', fontSize: '0.72rem' }}
            >
              Reset to visual
            </Button>
          }
        >
          This expression uses advanced syntax. Edit directly below, or reset to use the visual builder.
        </Alert>
      )}

      {/* Visual pipeline (hidden in advanced mode) */}
      {!pipeline.isAdvancedMode && (
        <>
          {/* Source fields */}
          {availableSourceFields.length > 0 && (
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  mb: 0.5,
                  display: 'block',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  fontSize: '0.65rem',
                }}
              >
                Source Fields
              </Typography>
              <SourceFieldSelector
                fields={availableSourceFields}
                selectedFieldId={selectedSourceFieldId}
                onSelect={handleSourceFieldSelect}
              />
            </Box>
          )}

          <Divider sx={{ my: 0.5 }} />

          {/* Pipeline steps */}
          <Box>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ mb: 0.75 }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  fontSize: '0.65rem',
                }}
              >
                Transformation Steps
              </Typography>
              {pipeline.steps.length > 0 && (
                <AddStepMenu onAdd={addStep} compact />
              )}
            </Stack>
            <Stack spacing={1}>
              <TransformationPipeline
                steps={pipeline.steps}
                sourceFields={availableSourceFields}
                onConfigChange={updateStepConfig}
                onRemove={removeStep}
                onReorder={reorderSteps}
                onAddStep={addStep}
              />
              {pipeline.steps.length > 0 && (
                <AddStepMenu onAdd={addStep} />
              )}
            </Stack>
          </Box>

          {/* Live preview */}
          <LivePreviewStrip
            sourceField={primarySourceField}
            steps={pipeline.steps}
            sampleData={sampleData}
          />
        </>
      )}

      {/* Advanced expression editor */}
      <AdvancedExpressionEditor
        expression={pipeline.isAdvancedMode ? initialExpression : expression}
        onChange={handleAdvancedExpressionChange}
        forceExpanded={pipeline.isAdvancedMode}
      />
    </Stack>
  );
};

export default VisualMappingBuilder;
